import React, {FC} from "react";

import "./ContactContentView.scss";
import {extractEmailAddresses, toTitleCase} from "../../util/Formatters";
import {HideContentButton} from "../common/HideContentButton";
import {ContactImage} from "./ContactImage";
import {StackPanel} from "../panels/StackPanel";
import classNames from "classnames";
import {SimpleField} from "../common/SimpleField";
import {ButtonPanel} from "../common/ButtonPanel";
import {DavProperty, EnrichedDavContact} from "../../services/messages/VCardContactData";
import {getDavMultiPropertyValue, getDavPropertyValue, KnownDavDisplayNames, KnownDavPropertyNames} from "../../util/DavUtil";
import {DateTime} from "luxon";

function renderContactDetailList(fieldName: string, details: string[] | null | undefined, highlightValue: string | undefined) {
    if (!details || details.length === 0 || details.every(d => !d || d.trim().length === 0)) return null;
    return (
        <div>
            <span className="field-name">{fieldName}</span>
            {details.map((detail, i) => (
                <span key={i}
                      className={classNames("field", {"highlighted": highlightValue === detail})}>
                    {detail}
                </span>))}
        </div>
    );
}

export interface ContactContentViewProps {
    contact: EnrichedDavContact;
}

export interface ContactContentLightViewProps {
    small?: boolean;
    highlightEmail?: string;
}

const SpecialContactProps: KnownDavPropertyNames[] = [
    "ADR",
    "CATEGORIES",
    "FN",
    "N",
    "NOTE",
    "NICKNAME",
    "EMAIL",
    "ORG",
    "PHOTO",
    "REV",
    "TEL",
    "UID",
    "URL",
    "VERSION",
];

const SingleDavPropertyValue = (
    {property, multiField, multiLine}: {
        property: DavProperty,
        multiLine: boolean,
        multiField: boolean,
    }) => {
    if (!property.value) {
        return null;
    }

    let fieldName = KnownDavDisplayNames[property.type] ?? property.type;
    const type = property.headers?.["TYPE"];
    if (type) {
        fieldName = `${toTitleCase(fieldName)} (${toTitleCase(type)})`;
    }

    if (property.type === "REV") {
        const dateTime = DateTime.fromISO(property.value);
        return <SimpleField fieldName={fieldName}
                            detail={dateTime.toLocaleString(DateTime.DATETIME_SHORT)}/>
    }

    if (multiLine) {
        return renderContactDetailList(fieldName, getDavMultiPropertyValue(property), undefined);
    }
    let stringValue = property.value;
    if (multiField) {
        stringValue = getDavMultiPropertyValue(property).join(" ");
    }
    return (
        <SimpleField fieldName={fieldName}
                     detail={stringValue}/>
    );
}

const DavPropertyFieldValues = (
    {propertyValues, multiField, multiLine}: {
        propertyValues: DavProperty[] | undefined,
        multiLine?: boolean,
        multiField?: boolean,
    }) => {
    if (!propertyValues) {
        return null;
    }
    return <>
        {propertyValues.map((propertyValue, index) => (
            <SingleDavPropertyValue
                key={index}
                property={propertyValue}
                multiField={!!multiField}
                multiLine={!!multiLine}/>
        ))}
    </>;
}

export const ContactContentLiteView: FC<ContactContentViewProps & ContactContentLightViewProps> = ({contact, small, highlightEmail}) => {
    const contactName = contact.DisplayName;

    // console.log("ContactContentLiteView", contact);

    const url = decodeURIComponent(getDavPropertyValue(contact, "URL") ?? "");
    return (
        <StackPanel
            className={classNames("contact-right-pane", {"small-pane": small})}
            orientation="vertical">

            {contactName && contactName !== "<unnamed contact>" && <div className="name">
                {small ? <ContactImage contact={contact} size="small"/> : null}
                {contactName}
            </div>}

            <DavPropertyFieldValues propertyValues={contact.props["N"]} multiField/>
            <SimpleField fieldName="Nickname" detail={getDavPropertyValue(contact, "NICKNAME")}/>
            <SimpleField fieldName="Notes" detail={getDavPropertyValue(contact, "NOTE")}/>

            {renderContactDetailList("Email", contact.Emails.map(e => extractEmailAddresses(e).slice(0, 1)[0]), highlightEmail)}

            <DavPropertyFieldValues propertyValues={contact.props["TEL"]} multiLine/>

            <DavPropertyFieldValues propertyValues={contact.props["ADR"]} multiLine/>

            {url && url?.trim().length > 0
                ? (<div>
                        <span className="field-name">Web Page</span>
                        <span className="field">
                                        <a href={url} target="_blank" rel="noopener noreferrer">{url}</a>
                                    </span>
                    </div>
                ) : null}

            {Object.entries(contact.props)
                .filter(p => !SpecialContactProps.includes(p[0] as KnownDavPropertyNames))
                .map(([field, props]) => <DavPropertyFieldValues key={field} propertyValues={props}/>)}

            <DavPropertyFieldValues propertyValues={contact.props["ORG"]} multiLine/>

            {/*TODO: Show REV formatted correctly*/}
            <DavPropertyFieldValues propertyValues={contact.props["REV"]} multiLine/>

            {/*TODO: Show Dates formatted correctly*/}
            {/*<SimpleField fieldName="Birthday" detail={contact.Birthday}/>*/}
            {/*<SimpleField fieldName="Anniversary" detail={contact.Anniversary}/>*/}

            {/*{renderContactDetailList("IM Address", contact.IMAddresses, highlightEmail)}*/}
        </StackPanel>
    );
}

export const ContactContentView: FC<ContactContentViewProps> = ({contact}) => {

    return (
        <ButtonPanel className="contact-content-container"
                     buttons={null}>
            <div>
                <HideContentButton/>
                <StackPanel className="contact-left-pane" orientation="vertical" align="middle">
                    <ContactImage contact={contact} size="large"/>

                    <div className="category"><span className="field">{contact.Category}</span></div>
                </StackPanel>

                <ContactContentLiteView contact={contact}/>
            </div>
        </ButtonPanel>
    );
};
